import {
  combineLatest as observableCombineLatest,
  forkJoin as observableForkJoin,
  of as observableOf,
  Observable,
  Subscription,
  ConnectableObservable,
  last,
  BehaviorSubject,
} from 'rxjs';

import {
  takeWhile,
  publishReplay,
  catchError,
  map,
  first,
  mergeMap,
  shareReplay,
} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {ResourceService} from '../../resource.service';
import {Resource} from '../../resource';
import {AbstractControlComponent} from './abstract-control';
import {LayoutService} from '../../../layout/layout.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ViewTabComponent} from '../viewtab.component';
import {AddViewEvent} from '../../events/addviewevent';
import {DatatableMultivalueDialogComponent} from '../../../shared/component/smd-datatable';
import {ValuesObject} from '../detail-tabconfig';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {MultivalueDialogComponent} from './multivalue-dialog/multivalue-dialog.component';
import {CacheService} from '../../../cache/cache.service';
import {MultivalueDialogResult} from './multivalue-dialog/multivalue-dialog-result';
import {LanguageService} from '../../../language/language.service';
import {SmdDataRowMark} from '../../../shared/component/smd-datatable';
import {multivalueRequired} from './validators/multivalue-required-validator';
import {DisplayAttribute} from '../../displayattribute';
import {ErrorService} from '../../../error/error.service';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';

@Component({
  selector: 'ctrl-identitypicker-multi',
  template: `
    <mat-chip-set class="chip-preview-list">
      @for (res of valueObservables; track res) {
        <mat-chip class="chip-clickable" (click)="onChipClick(res, $event)">
          {{ getCaughtObservable(getDisplayNameOfResource(res)) | async }}
        </mat-chip>
      }
      @if (remainingValueLength > 0) {
        <mat-chip class="chip-clickable chip-remaining" (click)="onRemainingClick()">
          <language
            key="CtrlIdentitypickerMultiMoreItems"
            defaultValue="… and {0} more."
            [args]="[remainingValueLength.toString()]"
          ></language>
        </mat-chip>
      }
      @if (
        (getCaughtObservable(isEnabled) | async) &&
        formControl.touched &&
        formControl.hasError('required')
      ) {
        <mat-error>
          <language key="ControlErrorRequired" defaultValue="This field is required."></language>
        </mat-error>
      }
    </mat-chip-set>
    @if (getCaughtObservable(isEnabled) | async) {
      <button mat-raised-button (click)="onManageClick()">
        <mat-icon>tune</mat-icon>
        <language key="CtrlIdentitypickerMultiManage" defaultValue="Manage"></language>
      </button>
    }
  `,
  styleUrls: ['./ctrl-identitypicker-multi.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlIdentitypickerMultiComponent
  extends AbstractControlComponent
  implements OnDestroy
{
  private observableCache: ConnectableObservable<Resource>[] = [];
  private observableConnections: Subscription[] = [];
  valueObservables: Observable<Resource>[] = null;
  remainingValueLength: number = null;

  @Input() objectType: string = null;
  @Input() objectID: string = null;
  @Input() objectDisplayName: string = null;

  static readonly defaultPreviewObjectCount: number = 10;

  private usageKeywords: string[] = [];

  constructor(
    parent: ViewTabComponent,
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private dialog: MatDialog,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    private cacheService: CacheService,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.observableConnections.forEach((s: Subscription) => s.unsubscribe());
  }

  getDisplayNameOfResource(resourceObs: Observable<Resource>): Observable<string> {
    if (resourceObs == null) return observableOf(null);
    return resourceObs.pipe(
      mergeMap((res: Resource) => {
        if (res == null || res[this.envConfig.getAttributeName('DisplayName')] == null) {
          return observableForkJoin(
            this.cacheService.getLanguageResource().pipe(first()),
            this.cacheService
              .getDisplayAttribute(
                this.envConfig.getAttributeName('DisplayName'),
                this.envConfig.getObjectTypeName('Resource'),
              )
              .pipe(first()),
            (lr: any, da: DisplayAttribute) => {
              return (
                '⟨ ' + LanguageService.translate('DatatableNo', 'No', lr) + ' ' + da.Caption + ' ⟩'
              );
            },
          );
        } else {
          return observableOf(res[this.envConfig.getAttributeName('DisplayName')]);
        }
      }),
    );
  }

  protected formatTarget(value: any): any {
    return value;
  }

  protected formatSource(source: any): any {
    this.valueObservables = [];
    if (source != null && Array.isArray(source)) {
      this.initPreview(source);
    } else {
      this.initPreview(null);
    }
    return source;
  }

  private initPreview(source: string[]) {
    if (source == null || source.length === 0) {
      this.valueObservables.length = 0;
      this.remainingValueLength = 0;
      this.changeDetector.markForCheck();
      return;
    }
    this.layoutService.updatePreloaderState('active');
    if (this._config.propertyExists('PreviewObjectCount')) {
      this._config
        .getPropertyValues('PreviewObjectCount')
        .pipe(
          first(),
          map((str: string) => Number(str)),
          catchError((error, caught) => this.handleError(error, caught)),
        )
        .subscribe((count: number) => {
          this.loadPreview(source, count);
        });
    } else {
      this.loadPreview(source, CtrlIdentitypickerMultiComponent.defaultPreviewObjectCount);
    }
  }

  private loadPreview(source: string[], count: number) {
    this.valueObservables.length = 0;
    for (let i = 0; i < source.length && i < count; i++) {
      const guid: string = source[i];
      let obs = this.observableCache[guid];
      if (obs == null) {
        obs = this.resourceService.getBasicResource(guid).pipe(publishReplay(1));
        this.observableConnections.push(obs.connect());
        this.observableCache[guid] = obs;
      }
      this.valueObservables.push(obs);
    }
    this.remainingValueLength = source.length - count;
    observableForkJoin(this.valueObservables)
      .pipe(catchError((error, caught) => this.handleError(error, caught)))
      .subscribe(x => this.layoutService.updatePreloaderState('hide'));
    this.changeDetector.markForCheck();
  }

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([multivalueRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });

    this._config
      .getPropertyValues('UsageKeywords')
      .pipe(
        map((strVal: string) => {
          if (strVal) return strVal.split(',');
          return null;
        }),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe((val: any) => {
        this.usageKeywords = val;
      });
  }

  onChipClick(resource: Observable<Resource>, event: MouseEvent): void {
    resource
      .pipe(
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe((res: Resource) => {
        if (res != null && res[this.envConfig.getAttributeName('ObjectID')] != null) {
          if (event.ctrlKey || event.button === 1) {
            window.open('#/get/' + res[this.envConfig.getAttributeName('ObjectID')]);
          } else {
            this._parent.onAddView.emit(
              new AddViewEvent('get', res[this.envConfig.getAttributeName('ObjectID')]),
            );
          }
        }
      });
  }

  onRemainingClick() {
    if (this.editEnabled) {
      this.onManageClick();
    } else {
      // open view dialog
      let sourceObs: Observable<ValuesObject<any>> = null;

      if (this._config.propertyExists('Source')) {
        sourceObs = this._config.getProperty('Source');
      } else if (
        this.sourceAlternateName != null &&
        this._config.propertyExists(this.sourceAlternateName)
      ) {
        sourceObs = this._config.getProperty(this.sourceAlternateName);
      } else {
        console.error('No source found for dialog');
        return;
      }

      if (sourceObs != null) {
        observableForkJoin(
          this._config.Caption.pipe(first()),
          sourceObs.pipe(first()),
          (captionVo: ValuesObject<string>, sourceVo: ValuesObject<any>) => {
            const dialogRef: MatDialogRef<DatatableMultivalueDialogComponent> = this.dialog.open(
              DatatableMultivalueDialogComponent,
            );
            dialogRef.componentInstance.displayName = this.objectDisplayName;
            dialogRef.componentInstance.attributeName = captionVo.Values;
            dialogRef.componentInstance.model = this.formControl.value;
            dialogRef.componentInstance.dataType = 'Reference';
            dialogRef.componentInstance.observable = this.resourceService.searchResources(
              '/' +
                this.envConfig.getObjectTypeName('Resource') +
                '[' +
                this.envConfig.getAttributeName('ObjectID') +
                '=/' +
                this.objectType +
                '[' +
                this.envConfig.getAttributeName('ObjectID') +
                "='" +
                this.objectID +
                "']/" +
                sourceVo.BindingPath +
                ']',
              [
                this.envConfig.getAttributeName('ObjectID'),
                this.envConfig.getAttributeName('DisplayName'),
              ].join(','),
              99999,
            );
            let open: boolean = true;
            dialogRef.componentInstance.openDetailEmitter
              .pipe(
                takeWhile(() => open),
                catchError((error, caught) => this.handleError(error, caught)),
              )
              .subscribe((val: {objectID: string; event: MouseEvent}) => {
                if (val.event.ctrlKey || val.event.button === 1) {
                  window.open('#/get/' + val.objectID);
                } else {
                  this._parent.onAddView.emit(new AddViewEvent('get', val.objectID));
                  dialogRef.close(null);
                  open = false;
                }
              });
            dialogRef
              .afterClosed()
              .pipe(
                first(),
                catchError((error, caught) => this.handleError(error, caught)),
              )
              .subscribe(() => (open = false));
          },
        )
          .pipe(
            first(),
            catchError((error, caught) => this.handleError(error, caught)),
          )
          .subscribe();
      }
    }
  }

  onManageClick() {
    if (this._config.propertyExists('UsageKeywords')) {
      this.manageWithUsageKeywords();
    } else if (this._config.propertyExists('Filter')) {
      this.manageWithFilter();
    }
  }

  private manageWithUsageKeywords(): void {
    if (this.usageKeywords == null || this.usageKeywords.length === 0) {
      this.openManageDialog(observableOf(null));
      return;
    }
    const sscsObs: Observable<SearchScopeConfiguration[]>[] = [];
    this.usageKeywords.forEach((keyword: string) => {
      sscsObs.push(this.cacheService.getSearchScopeConfigurationForObjectType(keyword));
    });

    const sscsObsJoined = observableForkJoin(sscsObs).pipe(
      map((sscss: SearchScopeConfiguration[][]) => {
        return [].concat.apply([], sscss);
      }),
      map((searchscopes: SearchScopeConfiguration[]) => {
        let newscopes = [];
        searchscopes.forEach((searchscope: SearchScopeConfiguration) => {
          let scope: SearchScopeConfiguration = {
            id: searchscope.id,
            displayname: searchscope.displayname,
            description: searchscope.description,
            usagekeyword: searchscope.usagekeyword,
            order: searchscope.order,
            attributesearched: searchscope.attributesearched,
            xpath: searchscope.xpath,
            resultobjecttype: searchscope.resultobjecttype,
            attributedisplayed: searchscope.attributedisplayed,
            sortingattribute: searchscope.sortingattribute,
            sortdirection: searchscope.sortdirection,
          };

          if (scope.xpath.includes('%ObjectID%')) {
            if (this.objectID !== null) {
              scope.xpath = scope.xpath.replace(/%ObjectID%/g, this.objectID);
            }
          }
          newscopes.push(scope);
        });
        return newscopes;
      }),
    );

    this.openManageDialog(sscsObsJoined);
  }

  private manageWithFilter(): void {
    //get first value, sometimes null when using expressions -> find other way
    this._config
      .getPropertyValues('Filter')
      .pipe(
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe();
    this._config
      .getPropertyValues('Filter')
      .pipe(
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe((filter: string) => {
        if (filter == null || filter.length === 0) {
          this.openManageDialog(observableOf(null));
          return;
        }
        const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
          id: null,
          displayname: null,
          xpath: filter,
          resultobjecttype: null,
          attributedisplayed: null,
        };

        if (this._config.propertyExists('MainSearchScreenText')) {
          this._config
            .getPropertyValues('MainSearchScreenText')
            .pipe(
              first(),
              catchError((error, caught) => this.handleError(error, caught)),
            )
            .subscribe((searchText: string) => {
              ssc.displayname = searchText;
              this.openManageDialog(observableOf([ssc]));
            });
        } else {
          this.openManageDialog(observableOf([ssc]));
        }
      });
  }

  private openManageDialog(searchScopesObs: Observable<SearchScopeConfiguration[]>): void {
    const resObjTypeObs = this._config.getPropertyValues('ResultObjectType');
    const columnsObs = this._config.getPropertyValues('ColumnsToDisplay');
    let sourceObs: Observable<ValuesObject<any>>;
    if (this._config.propertyExists('Source')) {
      sourceObs = this._config.getProperty('Source');
    } else if (this._config.propertyExists(this.sourceAlternateName)) {
      sourceObs = this._config.getProperty(this.sourceAlternateName);
    } else {
      console.error('No source binding found for manage dialog.');
      return;
    }
    const attributesToSearchObs = this._config.getPropertyValues('AttributesToSearch').pipe(
      map((str: string) => {
        if (str == null || str.trim().length === 0) {
          return [
            this.envConfig.getAttributeName('DisplayName'),
            this.envConfig.getAttributeName('Description'),
            this.envConfig.getAttributeName('ObjectType'),
          ];
        } else {
          return str.split(',').map(attr => attr.trim());
        }
      }),
    );
    const languageResObs = this.cacheService.getLanguageResource();
    const addAllowedObs = this._config.AddPermission;
    const removeAllowedObs = this._config.RemovePermission;

    observableCombineLatest(
      searchScopesObs,
      sourceObs,
      resObjTypeObs,
      columnsObs,
      attributesToSearchObs,
      languageResObs,
      addAllowedObs,
      removeAllowedObs,
      (
        sscs: SearchScopeConfiguration[],
        source: ValuesObject<any>,
        resObjType: string,
        columnsStr: string,
        attributesToSearch: string[],
        lr: any,
        addAllowed: boolean,
        removeAllowed: boolean,
      ) => {
        let columns: string[];
        if (columnsStr != null && columnsStr !== '') {
          columns = columnsStr.split(',').map(x => x.trim());
        } else {
          columns = [this.envConfig.getAttributeName('DisplayName')];
        }

        if (resObjType == null) {
          resObjType = 'Resource';
        }

        // /ResultObjectType[ObjectID=/ResourceObjectType[ObjectID='ResourceObjectID']/Attribute]
        let xpath: string = null;
        if (this.objectID != null && this.objectType != null) {
          xpath =
            '/' +
            resObjType +
            '[' +
            this.envConfig.getAttributeName('ObjectID') +
            '=/' +
            this.objectType +
            '[' +
            this.envConfig.getAttributeName('ObjectID') +
            "='" +
            this.objectID +
            "']/" +
            source.BindingPath +
            ']';
        }
        const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
          id: null,
          displayname: LanguageService.translate('Search', 'Search', lr),
          xpath: xpath,
          resultobjecttype: resObjType,
          attributedisplayed: columns,
          attributesearched: attributesToSearch,
        };

        if (sscs != null && sscs.length === 1) {
          if (sscs[0].displayname == null) {
            sscs[0].displayname = LanguageService.translate('Search', 'Search', lr);
          }
          if (sscs[0].resultobjecttype == null) {
            sscs[0].resultobjecttype = resObjType;
          }
          if (sscs[0].attributedisplayed == null) {
            sscs[0].attributedisplayed = columns;
          }

          if (sscs[0].attributesearched == null) {
            sscs[0].attributesearched = attributesToSearch;
          }
        }

        if (sscs == null || sscs.length === 0) {
          addAllowed = false;
        }

        let dialogRef: MatDialogRef<MultivalueDialogComponent> =
          this.dialog.open(MultivalueDialogComponent);
        dialogRef.componentInstance.addAllowed = addAllowed === true;
        dialogRef.componentInstance.removeAllowed = removeAllowed === true;
        if (
          source.SpecialObject != null &&
          source.SpecialObject instanceof MultivalueDialogResult
        ) {
          dialogRef.componentInstance.setSelectedValues(
            source.SpecialObject.CurrentlySelected,
            source.SpecialObject.OriginalSelected,
          );
          dialogRef.componentInstance.setChanges(source.SpecialObject.Changes);
        } else {
          dialogRef.componentInstance.setSelectedValues(source.Values);
        }
        if (this._config.propertyExists('ListViewAddTitle')) {
          this._config
            .getPropertyValues('ListViewAddTitle')
            .pipe(
              first(),
              catchError((error, caught) => this.handleError(error, caught)),
            )
            .subscribe((title: string) => {
              dialogRef.componentInstance.addListTitle = title;
            });
        }
        dialogRef.componentInstance.objectDisplayName = this.objectDisplayName;
        dialogRef.componentInstance.attributeLabel = this.getCaughtObservable(
          this._config.Caption.pipe(map(vo => (vo ? vo.Values : null))),
        );
        dialogRef.componentInstance.SearchScopes = sscs;
        dialogRef.componentInstance.SelectedSearchScope = ssc;
        dialogRef
          .afterClosed()
          .pipe(
            first(),
            catchError((error, caught) => this.handleError(error, caught)),
          )
          .subscribe((result: MultivalueDialogResult) => {
            if (result != null) {
              this.formControl.setValue(result);
              this.formControl.markAsTouched();
              const addedGuids = result.Changes.filter(rm => rm.mark === SmdDataRowMark.Add).map(
                rm => rm.model.ObjectID,
              );
              this.cacheService.addRecentGuids(addedGuids);
              this.initPreview(result.CurrentlySelected);
            }
          });
      },
    )
      .pipe(
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe();
  }
}
